// client/src/components/stats/Cbet3BetPotBreakdown.tsx
//
// Muestra el breakdown de CBet en pozos 3beteados: IP vs OOP.
// Solo se muestra cuando Hero fue el 3bettor (PFA en pozo 3beteado).

import React from 'react';
import { useT } from '../../lib/i18n';
import { StackKey } from './leakRanges';
import { useBenchmarks } from '../../contexts/BenchmarkContext';

interface Cbet3BetPotBreakdownProps {
  ip: number;
  ipOpp: number;
  oop: number;
  oopOpp: number;
  activeStack: StackKey;
  label?: string;
}

const MIN_SAMPLE = 20;

export function Cbet3BetPotBreakdown({
  ip, ipOpp, oop, oopOpp, activeStack, label,
}: Cbet3BetPotBreakdownProps) {
  const { t } = useT();
  const bm = useBenchmarks();

  const totalOpp = ipOpp + oopOpp;

  if (totalOpp === 0) {
    return (
      <div className="rfi-breakdown rfi-empty">
        <p className="rfi-empty-text">{t('stats.cbet3BetPot.empty')}</p>
      </div>
    );
  }

  const rows = [
    { key: 'cbet3BetPotIP',  label: 'IP',  pct: ip,  opp: ipOpp  },
    { key: 'cbet3BetPotOOP', label: 'OOP', pct: oop, opp: oopOpp },
  ];

  return (
    <div className="rfi-breakdown">
      {label && <div className="rfi-breakdown-title">{label}</div>}

      <table className="rfi-table">
        <thead>
          <tr>
            <th>{t('stats.cbet3BetPot.thPos')}</th>
            <th className="rfi-th-num">{t('stats.cbet3BetPot.thCbet')}</th>
            <th className="rfi-th-num">{t('stats.cbet3BetPot.thBenchmark')}</th>
            <th className="rfi-th-num">{t('stats.cbet3BetPot.thSample')}</th>
            <th className="rfi-th-status" />
          </tr>
        </thead>
        <tbody>
          {rows.map(row => {
            const lowSample = row.opp < MIN_SAMPLE;
            const status = lowSample || row.opp === 0
              ? 'ok'
              : bm.evalStat(row.key, row.pct, activeStack);
            const showDot = !lowSample && row.opp > 0;
            const r = bm.getRange(row.key, activeStack);
            const bench = r ? `${r.min}–${r.max}%` : '—';

            return (
              <tr
                key={row.key}
                className={`rfi-row rfi-row-${status} ${lowSample ? 'rfi-row-low' : ''}`}
              >
                <td className="rfi-cell-pos">{row.label}</td>
                <td className="rfi-cell-num rfi-cell-value">
                  {row.opp > 0 ? `${row.pct}%` : '—'}
                </td>
                <td className="rfi-cell-num rfi-cell-benchmark">{bench}</td>
                <td className="rfi-cell-num rfi-cell-sample">
                  {row.opp > 0 ? `n=${row.opp}` : '—'}
                  {lowSample && row.opp > 0 && (
                    <span className="rfi-low-tag"> {t('stats.cbet3BetPot.lowTag')}</span>
                  )}
                </td>
                <td className="rfi-cell-status">
                  {showDot && <span className={`stat-dot dot-${status}`} />}
                </td>
              </tr>
            );
          })}
        </tbody>
      </table>

      <p className="cbet-texture-disclaimer">{t('stats.cbet3BetPot.disclaimer')}</p>
    </div>
  );
}
